import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';

const SECTIONS = [
  { id: 'overview',        label: 'Platform Overview' },
  { id: 'intake-lifecycle', label: 'Intake Lifecycle' },
  { id: 'applications',    label: 'Applications' },
  { id: 'aptitude-tests',  label: 'Academic Assessments' },
  { id: 'email-system',    label: 'Email System' },
  { id: 'site-settings',   label: 'Site Settings' },
  { id: 'cohorts',         label: 'Cohorts' },
  { id: 'students',        label: 'Students' },
  { id: 'email-logs',      label: 'Email Logs' },
  { id: 'audit-logs',      label: 'Audit Logs' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-6 mb-12">
      <h2 className="text-xl font-playfair font-bold text-primary border-b border-border pb-2 mb-5">{title}</h2>
      <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="mt-5">
      <h3 className="font-semibold text-base text-gray-900 mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function StageCard({ stage, badge, email, rollback, description }) {
  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex items-center gap-3 mb-2">
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge}`}>{stage}</span>
        {rollback && <span className="text-xs text-muted-foreground">← can roll back</span>}
      </div>
      <p>{description}</p>
      {email && (
        <p className="mt-2 text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded">
          <strong>Emails sent:</strong> {email}
        </p>
      )}
    </div>
  );
}

function InfoBox({ title, children, color = 'blue' }) {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-900',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    red:    'bg-red-50 border-red-200 text-red-900',
    green:  'bg-green-50 border-green-200 text-green-900',
  };
  return (
    <div className={`border rounded-lg px-4 py-3 ${colors[color]}`}>
      {title && <p className="font-semibold mb-1">{title}</p>}
      <div className="text-sm space-y-1">{children}</div>
    </div>
  );
}

function Placeholder({ name }) {
  return <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{`{{${name}}}`}</code>;
}

export default function AdminGuidePage() {
  const [activeSection, setActiveSection] = useState('overview');
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex min-h-screen bg-muted">
      {/* ── Sticky TOC ─────────────────────────────────── */}
      <aside className="hidden lg:flex flex-col w-56 shrink-0 sticky top-0 h-screen overflow-y-auto py-6 px-4 border-r border-border bg-white">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-4">Contents</p>
        <nav className="space-y-0.5">
          {SECTIONS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors
                ${activeSection === id
                  ? 'bg-primary text-white font-medium'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </aside>

      {/* ── Content ────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="mb-10">
            <h1 className="text-3xl font-playfair font-bold text-primary">Admin Operations Guide</h1>
            <p className="text-muted-foreground mt-2">
              Everything you need to know about running the FB Attorneys Academy admissions portal.
            </p>
          </div>

          {/* ── Platform Overview ── */}
          <Section id="overview" title="Platform Overview">
            <p>
              The FB Attorneys Academy Portal manages the full admissions cycle — from publicising an intake
              and receiving applications, through shortlisting and the Academic Assessment process, to final enrollment.
              It also provides a student learning area for enrolled cohorts.
            </p>

            <SubSection title="Key Roles">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Admin</strong> — full access to all portal features via this sidebar.</li>
                <li><strong>Student</strong> — read-only access to their own class sessions, materials, and progress. (Student login is enabled after enrollment in a future phase.)</li>
                <li><strong>Applicant</strong> — not a login user; submits the application form publicly and receives emails.</li>
              </ul>
            </SubSection>

            <SubSection title="High-Level Admissions Flow">
              <ol className="list-decimal pl-5 space-y-1">
                <li>Admin creates an <strong>Intake Period</strong> and opens it.</li>
                <li>Applicants fill the public application form; ineligible applicants are auto-rejected.</li>
                <li>Admin reviews applications, stars shortlisted ones, then advances the intake to <strong>Shortlisted</strong> — this bulk-sends shortlist and decline emails.</li>
                <li>Admin advances to <strong>Aptitude Sent</strong> — assessment links are generated and emailed to shortlisted applicants.</li>
                <li>Applicants complete the timed Academic Assessment online.</li>
                <li>Admin advances to <strong>Enrolling</strong> and bulk-enrolls passing applicants into a Cohort.</li>
                <li>Admin advances to <strong>Completed</strong> — welcome emails go to enrolled applicants; rejection emails to the rest.</li>
              </ol>
            </SubSection>
          </Section>

          {/* ── Intake Lifecycle ── */}
          <Section id="intake-lifecycle" title="Intake Lifecycle">
            <p>
              Each intake moves through seven statuses. The action buttons on the Intake Periods page
              advance the intake forward; ghost "↩" buttons allow rollbacks with appropriate warnings.
            </p>

            <div className="space-y-3 mt-3">
              <StageCard
                stage="Draft"
                badge="bg-yellow-100 text-yellow-800"
                description="Intake created but not yet visible to applicants. Use this stage to configure dates, capacity, and Academic Assessment settings before going live."
              />
              <StageCard
                stage="Open"
                badge="bg-green-100 text-green-800"
                rollback
                description="Applications are accepted. The public landing page shows the active intake name and deadline. Applicants can submit the form while the intake is open."
              />
              <StageCard
                stage="Reviewing"
                badge="bg-blue-100 text-blue-800"
                rollback
                description='Applications are closed. Admin reviews all applications in the Applications tab, sorted by score. Use the ★ star button to shortlist individual applicants. Click "Complete Review" when ready.'
              />
              <StageCard
                stage="Shortlisted"
                badge="bg-purple-100 text-purple-800"
                rollback
                email="Shortlist email → starred applicants. Decline email → remaining eligible applicants. Auto-rejected (ineligible) applicants are skipped."
                description="Sent when admin clicks Complete Review. Application statuses are updated: starred → screening, others → rejected."
              />
              <StageCard
                stage="Tests Sent"
                badge="bg-indigo-100 text-indigo-800"
                rollback
                email="Assessment link email → all shortlisted (screening) applicants. Each link is unique and expires after the configured assessment window."
                description='Triggered by "Send Assessments". A unique token is generated per applicant. The link is valid for aptitude_test_duration_hours (default 2 hours).'
              />
              <StageCard
                stage="Enrolling"
                badge="bg-orange-100 text-orange-800"
                rollback
                description='Admin uses the "Bulk Enroll" button to enroll all passing applicants into a selected Cohort. Already-enrolled applicants are skipped. Enrollment creates a Student record.'
              />
              <StageCard
                stage="Completed"
                badge="bg-gray-200 text-gray-700"
                rollback
                email="Welcome email → enrolled applicants. Rejection email → shortlisted applicants not enrolled."
                description='Triggered by "Complete Intake". Marks the intake as fully processed.'
              />
            </div>

            <SubSection title="Creating an Intake">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Name</strong> — e.g. "Intake 2026-A". Shown to applicants on the landing page.</li>
                <li><strong>Applications Open / Close Date</strong> — displayed on the landing page as the deadline.</li>
                <li><strong>Target Size</strong> — how many applicants you aim to shortlist (informational).</li>
                <li><strong>Enrollment Capacity</strong> — maximum students to enroll.</li>
                <li><strong>Academic Assessment Date & Time</strong> — shown to shortlisted applicants in their email.</li>
                <li><strong>Assessment Window (hours)</strong> — how long the assessment link stays valid after the applicant opens it. Default 2 hours.</li>
              </ul>
            </SubSection>

            <SubSection title="Editing an Intake">
              <p>Click the pencil icon on any intake row to update any of the above fields at any stage.</p>
            </SubSection>

            <SubSection title="Rollbacks">
              <p>
                Ghost "↩" buttons on each intake row allow rolling back to the previous status.
                Rolling back from <strong>Shortlisted</strong> or <strong>Tests Sent</strong> shows an orange warning:
                emails already sent to applicants cannot be unsent. The rollback only changes the intake status
                and does not modify individual application statuses.
              </p>
            </SubSection>

            <SubSection title="Funnel Stats (📊)">
              <p>Click the chart button on any intake to expand a funnel showing application counts per status.</p>
            </SubSection>
          </Section>

          {/* ── Applications ── */}
          <Section id="applications" title="Applications">
            <p>
              The Applications page lists all applications for the selected intake, sorted by score (highest first),
              with rejected/ineligible applicants pushed to the bottom.
            </p>

            <SubSection title="Eligibility & Auto-Rejection">
              <p>On submission the system evaluates each applicant against three criteria:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Age ≤ 25 at time of submission</li>
                <li>A-Level Division 1 (flexible string matching)</li>
                <li>LLB GPA ≥ 3.3</li>
              </ul>
              <p>
                Applicants who fail any criterion are immediately set to <code className="bg-gray-100 px-1 rounded text-xs">rejected</code> and
                receive an ineligibility email automatically. They appear greyed-out in the applications table.
              </p>
            </SubSection>

            <SubSection title="Scoring">
              <p>Each eligible application receives an automated score (0–100) based on configurable weights:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>GPA</strong> — default 50%. Linear scale from 3.3 (0 pts) to 4.0 (max pts).</li>
                <li><strong>A-Level grades</strong> — default 30%. Top 3 subjects; A=5 pts, B=4, C=3, D=2, E=1, S=0.</li>
                <li><strong>O-Level division</strong> — default 20%. Division 1=4 pts, 2=3, 3=2, 4=1.</li>
              </ul>
              <p>Configure weights in <strong>Site Settings → Scoring Weights</strong>. Weights must sum to 100.</p>
            </SubSection>

            <SubSection title="Shortlisting">
              <p>
                During the Reviewing stage, click the <strong>★ star icon</strong> in the Shortlist column
                to mark an applicant. Stars are toggled individually. The rank column helps you decide cut-off points.
              </p>
            </SubSection>

            <SubSection title="Viewing an Application">
              <p>
                Click any row to open the <strong>Application Review</strong> page. This shows all submitted fields
                across four tabs: Details, Documents, Checklist, and Emails.
              </p>
            </SubSection>

            <SubSection title="Manual Email Sending">
              <p>
                Inside the application review page, go to the <strong>Emails</strong> tab.
                You can manually send any of the following template emails to this specific applicant:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li><strong>eligible</strong> — confirmation that their application was received and is eligible</li>
                <li><strong>ineligible</strong> — explains why they don't meet criteria</li>
                <li><strong>shortlisted</strong> — invitation to the Academic Assessment with test date/time</li>
                <li><strong>not_shortlisted</strong> — polite decline after review</li>
                <li><strong>test_link</strong> — sends their unique Academic Assessment URL</li>
                <li><strong>rejection</strong> — final rejection after Academic Assessment stage</li>
                <li><strong>welcome</strong> — enrollment welcome email</li>
              </ul>
              <p className="mt-1">Each button shows the last date that email was sent to this applicant.</p>
            </SubSection>

            <SubSection title="Email History">
              <p>
                The Emails tab also shows a chronological history of all emails sent to this applicant,
                including template name, subject, delivery status (sent / delivered / failed / bounced),
                and timestamps for when it was sent, delivered, and opened.
              </p>
            </SubSection>

            <SubSection title="Review Checklist">
              <p>
                The Checklist tab shows a configurable set of checklist items the admin should verify
                before marking an application as reviewed. Configure the checklist items in
                <strong> Site Settings → Review Checklist</strong>.
              </p>
            </SubSection>
          </Section>

          {/* ── Academic Assessments ── */}
          <Section id="aptitude-tests" title="Academic Assessments">
            <p>
              The Academic Assessment is a timed, passage-based multiple-choice test taken by shortlisted applicants
              via a unique URL sent to their email.
            </p>

            <SubSection title="Test Format">
              <ul className="list-disc pl-5 space-y-1">
                <li>6 reading passages (450–650 words each)</li>
                <li>4–5 MCQ questions per passage</li>
                <li>60-minute total time limit</li>
                <li>Score threshold for passing is configurable in Site Settings → Admissions</li>
              </ul>
            </SubSection>

            <SubSection title="Passage Bank Setup">
              <p>
                Before sending tests, you must populate the question bank in
                <strong> Site Settings → Question Bank</strong>.
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Click <strong>Add Passage</strong> and enter the passage text (450–650 words).</li>
                <li>The word count indicator turns green when within range, amber otherwise.</li>
                <li>Use <code className="bg-gray-100 px-1 rounded text-xs">**text**</code> in the passage to render bold text.</li>
                <li>Under each passage, add 4–5 questions with options A–E and mark the correct answer.</li>
                <li>A minimum of 6 passages with questions is required before assessments can be sent.</li>
              </ul>
            </SubSection>

            <SubSection title="Assessment Link & Expiry">
              <p>
                When the intake advances to <strong>Tests Sent</strong>, each shortlisted applicant receives
                a unique URL containing a secure token. The link is valid for the number of hours set in
                the intake's "Assessment Window" field (default: 2 hours from when the applicant first opens the link).
              </p>
              <p>
                If the applicant opens the link after it has expired, they see an error and receive a score of 0
                automatically. You can resend a fresh assessment link manually from the applicant's Emails tab.
                Resending revokes the old token and generates a new one.
              </p>
            </SubSection>

            <SubSection title="Browser Proctoring">
              <p>The test page enforces the following automatically:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Fullscreen mode is required. Exiting fullscreen counts as a violation.</li>
                <li>Tab switching or window blur counts as a violation (tracked via visibility events).</li>
                <li>Copy, paste, and right-click are disabled.</li>
                <li>Common keyboard shortcuts (Ctrl+C, Ctrl+V, Ctrl+A, etc.) are blocked.</li>
                <li>Mobile devices are blocked from taking the test.</li>
                <li>After <strong>5 severe violations</strong>, the test auto-submits with whatever answers have been given.</li>
              </ul>
              <p className="mt-1">Violation logs are stored and visible in the application's test record in the database.</p>
            </SubSection>

            <SubSection title="Draft Saving">
              <p>
                The test automatically saves progress to localStorage on every answer change and to the server
                every 60 seconds. If the browser crashes or the window closes, the applicant can reopen the
                same link and resume from their last saved position.
              </p>
            </SubSection>

            <SubSection title="Per-Passage Timing">
              <p>
                Time spent on each passage is tracked and stored with the submission. This data is available
                in the database for analysis.
              </p>
            </SubSection>
          </Section>

          {/* ── Email System ── */}
          <Section id="email-system" title="Email System">
            <p>
              All outbound emails use <strong>SendGrid</strong>. Templates are configurable from
              Site Settings → Email Templates.
            </p>

            <SubSection title="Template Keys & When They Fire">
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left px-3 py-2 border">Key</th>
                      <th className="text-left px-3 py-2 border">When Fired</th>
                      <th className="text-left px-3 py-2 border">Trigger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['eligible',         'Application received and eligible',             'Auto — on form submission (eligible)'],
                      ['ineligible',       'Application does not meet criteria',            'Auto — on form submission (ineligible)'],
                      ['shortlisted',      'Invited to Academic Assessment',           'Bulk — intake advances to Shortlisted'],
                      ['not_shortlisted',  'Not shortlisted after review',                  'Bulk — intake advances to Shortlisted'],
                      ['test_link',        'Unique Academic Assessment link',          'Bulk — intake advances to Tests Sent'],
                      ['rejection',        'Final rejection after test stage',              'Bulk — intake advances to Completed'],
                      ['welcome',          'Enrollment confirmation',                       'Bulk — intake advances to Completed'],
                    ].map(([key, desc, trigger]) => (
                      <tr key={key}>
                        <td className="px-3 py-2 border font-mono bg-gray-50">{key}</td>
                        <td className="px-3 py-2 border">{desc}</td>
                        <td className="px-3 py-2 border text-muted-foreground">{trigger}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SubSection>

            <SubSection title="Template Placeholders">
              <p>
                In the template editor, use these placeholders — they will be replaced with real values
                when the email is sent:
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  'first_name','last_name','email','intake_name',
                  'aptitude_test_datetime','aptitude_test_duration_hours',
                  'test_link','aptitude_score','cohort_name',
                ].map(p => <Placeholder key={p} name={p} />)}
              </div>
            </SubSection>

            <SubSection title="Manual Email Sending">
              <p>
                From any application's review page → Emails tab, you can send any template email to that
                individual applicant at any time. Useful for resending an assessment link, sending a correction,
                or following up with a specific applicant.
              </p>
            </SubSection>

            <SubSection title="Bulk Email">
              <p>
                The <strong>Bulk Email</strong> page (sidebar) lets you send a custom message to a filtered
                segment of applicants (e.g. all accepted applicants in a specific intake) without
                triggering a stage transition.
              </p>
            </SubSection>

            <SubSection title="Email Logs">
              <p>
                The <strong>Email Logs</strong> page shows all outbound emails with status, delivery timestamps,
                and bounce reasons. Each log entry is also accessible from the individual applicant's Emails tab.
              </p>
            </SubSection>
          </Section>

          {/* ── Site Settings ── */}
          <Section id="site-settings" title="Site Settings">
            <p>
              Accessed via <strong>Site Settings</strong> in the sidebar. Each tab controls a different
              aspect of the platform.
            </p>

            <SubSection title="Landing Page">
              <p>
                Edit the content shown on the public homepage: hero heading, subheading, about-us text,
                programme name, programme description, and the call-to-action button. Changes are saved
                immediately.
              </p>
            </SubSection>

            <SubSection title="FAQs">
              <p>
                Add, edit, and reorder Frequently Asked Questions displayed on the public landing page.
                Supports rich text answers.
              </p>
            </SubSection>

            <SubSection title="Eligibility">
              <p>
                Edit the eligibility criteria text shown to applicants on the application form and
                landing page. Note: the actual eligibility logic (age ≤ 25, A-Level Div 1, GPA ≥ 3.3)
                is enforced in the backend and is separate from this display text.
              </p>
            </SubSection>

            <SubSection title="Declarations">
              <p>
                Configure the declaration statements that applicants must agree to on Step 5 of
                the application form (e.g. truthfulness declarations, consent to data processing).
              </p>
            </SubSection>

            <SubSection title="Terms & Conditions">
              <p>
                Edit the full terms and conditions text shown at <code className="bg-gray-100 px-1 rounded text-xs">/terms-and-conditions</code>.
                Linked from the application form.
              </p>
            </SubSection>

            <SubSection title="Admissions">
              <p>Control the admissions toggle and test pass threshold:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li><strong>Accept Applications</strong> — toggle on/off. When off, the application form shows a "not currently accepting" message.</li>
                <li><strong>Assessment Pass Score</strong> — minimum percentage (0–100) required to be considered "passing" the Academic Assessment. Default: 70%.</li>
              </ul>
            </SubSection>

            <SubSection title="Question Bank">
              <p>
                Manage reading passages and their MCQ questions for the Academic Assessment.
                See the <strong>Academic Assessments</strong> section above for full setup instructions.
              </p>
            </SubSection>

            <SubSection title="Email Templates">
              <p>
                Edit the subject line and body of each of the 7 email templates. Bodies support HTML
                and template placeholders (listed in the Email System section above).
                Click <strong>Reset to Default</strong> on any template to restore the original text.
              </p>
              <InfoBox color="yellow" title="Important">
                <p>
                  Always test edited templates by using the manual send button on a test application
                  before sending a bulk transition email.
                </p>
              </InfoBox>
            </SubSection>

            <SubSection title="Review Checklist">
              <p>
                Define the list of checklist items shown on each application's Checklist tab during review.
                Examples: "Documents verified", "Referee contacted", "Score reviewed".
              </p>
            </SubSection>

            <SubSection title="Scoring Weights">
              <p>Configure the three components of the application score:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li><strong>GPA Weight</strong> — contribution of LLB GPA to total score.</li>
                <li><strong>A-Level Weight</strong> — contribution of A-Level grades (top 3 subjects).</li>
                <li><strong>O-Level Weight</strong> — contribution of O-Level division.</li>
              </ul>
              <p className="mt-1">
                The three weights must sum to exactly 100. The system validates this on save.
                Changing weights recalculates all existing application scores.
              </p>
            </SubSection>

            <SubSection title="System Config">
              <p>
                Low-level system settings such as the SendGrid API key, sender email address, and
                application email address. Changes here affect all outgoing emails immediately.
              </p>
              <InfoBox color="red" title="Caution">
                <p>Incorrect SendGrid credentials will silently prevent all emails from being delivered.</p>
              </InfoBox>
            </SubSection>

            <SubSection title="Dev Tools">
              <p>
                Utilities for development and testing only. Includes the ability to re-initialise
                default email templates and reset certain database collections. Do not use in production
                unless advised.
              </p>
            </SubSection>
          </Section>

          {/* ── Cohorts ── */}
          <Section id="cohorts" title="Cohorts">
            <p>
              Cohorts represent a group of enrolled students following the same programme schedule.
              Each intake's enrolled students are assigned to a cohort.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Create a cohort before bulk-enrolling applicants (you will need the cohort ID during enrollment).</li>
              <li>Each cohort has a name, start date, and optional description.</li>
              <li>Class sessions and materials are associated with cohorts, not individual students.</li>
              <li>Students see only the sessions and materials belonging to their cohort.</li>
            </ul>
          </Section>

          {/* ── Students ── */}
          <Section id="students" title="Students">
            <p>
              The Students page lists all enrolled students across all cohorts. Each student record
              is created automatically when an applicant is bulk-enrolled.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Student records include name, email, cohort, and enrollment date.</li>
              <li>Student login credentials (username/password) are not issued in this phase.
                This will be added in a future update.</li>
              <li>You can view a student's profile and associated application from this page.</li>
            </ul>
          </Section>

          {/* ── Email Logs ── */}
          <Section id="email-logs" title="Email Logs">
            <p>
              A complete log of every email sent by the platform. Useful for auditing, debugging
              delivery failures, and confirming that bulk sends completed successfully.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Status</strong>: <span className="bg-blue-100 text-blue-700 px-1 rounded text-xs">sent</span> → <span className="bg-green-100 text-green-700 px-1 rounded text-xs">delivered</span> or <span className="bg-orange-100 text-orange-700 px-1 rounded text-xs">bounced</span> / <span className="bg-red-100 text-red-700 px-1 rounded text-xs">failed</span></li>
              <li>Logs are also visible per-applicant on the application's Emails tab.</li>
              <li>Manually sent emails are marked with a "manual" flag.</li>
              <li>Use the search/filter controls to find emails by recipient, template, or date.</li>
            </ul>
          </Section>

          {/* ── Audit Logs ── */}
          <Section id="audit-logs" title="Audit Logs">
            <p>
              A chronological record of all significant admin actions taken on the platform.
              Useful for accountability and tracing changes.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Recorded events include: intake status changes, application status changes,
                shortlist toggles, bulk enrollments, settings changes, and user logins.</li>
              <li>Each entry shows the timestamp, admin email, action type, and the affected entity.</li>
              <li>Logs are append-only and cannot be deleted from the UI.</li>
            </ul>

            <InfoBox color="green" title="Tip">
              <p>
                If something unexpected happens (e.g. an email went to the wrong group, or an intake
                status changed unexpectedly), check the Audit Logs first to see what action triggered it.
              </p>
            </InfoBox>
          </Section>

          <div className="pb-16" />
        </div>
      </div>
    </div>
  );
}
